import type { createFeedbackBodyZod } from "@nlc/shared-types";
import type * as z from "zod";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import type { GetFeedbackResponse } from "@/features/request/feedback-tool/feedbackTool/hooks/useGetStories.tsx";

import { toast } from "@/components/toast/toast-queue.ts";
import { useFeedbackTool } from "@/features/request/feedback-tool/feedbackTool/hooks/useFeedbackTool.tsx";
import { feedbackToolKeys, requestKeys } from "@/hooks/queryKeys";
import { useIsTemplate } from "@/hooks/useIsTemplate";
import apiClient from "@/lib/api/client";

export type CreateFeedbackFormData = z.infer<typeof createFeedbackBodyZod>;

export function useCreateFeedback(parentId: number, requestId: number) {
  const queryClient = useQueryClient();
  const { setFormFeedBack } = useFeedbackTool();
  const { isTemplate } = useIsTemplate();

  return useMutation({
    mutationFn: async (data: CreateFeedbackFormData) => {
      if (isTemplate) {
        return {
          attachments:
            data.attachments?.map((att, index) => ({
              attachmentId: `temp-${Date.now()}-${index}`,
              name: att.name || "attachment",
              storyId: Date.now(),
              url: "",
            })) || [],
          comment: data.comment,
          commentBatchId: "",
          coordinates: data.coordinates,
          createdAt: new Date().toISOString(),
          id: Date.now(),
          isEdited: false,
          isResolved: false,
          parentId: parentId,
          updatedAt: new Date().toISOString(),
          user: {
            id: 1,
            name: "Demo User",
          },
        };
      }

      return apiClient
        .url(`/stories/${parentId}/feedback-tool`)
        .post(data)
        .json();
    },
    mutationKey: feedbackToolKeys.createFeedback(parentId),
    onError: (error: unknown) => {
      setFormFeedBack(null);

      const isResolvedConflict =
        typeof error === "object" &&
        error !== null &&
        "statusCode" in error &&
        (error as { statusCode?: number }).statusCode === 409;

      toast.add(
        {
          description: isResolvedConflict
            ? "This media already has resolved feedback and can no longer receive new feedback."
            : "Something went wrong while saving your feedback. Please try again.",
          title: "Unable to save feedback",
          type: "error",
        },
        {
          timeout: 5000,
        },
      );
    },
    onSuccess: async (newFeedback) => {
      setFormFeedBack(null);

      toast.add(
        {
          title: "Feedback saved successfully",
          type: "success",
        },
        {
          timeout: 5000,
        },
      );

      if (isTemplate) {
        queryClient.setQueryData<GetFeedbackResponse>(
          feedbackToolKeys.feedbacks(parentId),
          (old) => {
            const currentData = old || [];

            return [...currentData, newFeedback] as GetFeedbackResponse;
          },
        );
      } else {
        await queryClient.invalidateQueries({
          queryKey: feedbackToolKeys.feedbacks(parentId),
        });

        await queryClient.invalidateQueries({
          queryKey: requestKeys.requestById(requestId),
        });
      }
    },
  });
}
